import React, { useEffect, useMemo, useState } from 'react';
import { CapType, updateCapType } from '../../api/captype';
import { Device, deviceLabel, listAllDevices } from '../../api/device';
import { listProcessArchs, ProcessArch } from '../../api/parameter';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { useDictText } from '../../components/DictSelect';
import Modal from '../../components/Modal';
import { alertDialog } from '../../utils/dialog';

// 工艺类型 ↔ 设备显式绑定弹窗 (2026-09-08): 全量设备按工艺框架分组勾选, 保存整体替换 cap_type.deviceKeys.
// 候选不限框架 —— 非激光设备 (knife/thermal/inkjet...) 也能挂进工艺类型, 这正是改显式绑定的动机.
// 停用设备照常可勾 (绑定关系保留, 反查/徽标只展示启用中的).

interface Props {
    /** 为 null 时不显示 */
    cap: CapType | null;
    onClose: () => void;
    /** 保存成功 (含转审批) 后回调, 让列表回读 */
    onSaved: () => void;
}

// 全量设备 + 框架清单在弹窗多次打开间复用 (模块级缓存, 与 CapTypeBadges 的 capTypes 缓存同思路;
// 设备增删是低频操作, 刷新页面即重新拉取)
let devicesCache: Device[] | null = null;
let archsCache: ProcessArch[] | null = null;

function CapDeviceBindModal({ cap, onClose, onSaved }: Props) {
    const [devices, setDevices] = useState<Device[]>(devicesCache || []);
    const [archs, setArchs] = useState<ProcessArch[]>(archsCache || []);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [keyword, setKeyword] = useState('');
    const [busy, setBusy] = useState(false);
    const lightText = useDictText('light_source');

    const capKey = cap?.capKey || '';
    // 打开时: 以当前绑定初始化勾选; 首次拉全量设备 + 框架
    useEffect(() => {
        if (!capKey) return;
        setSelected(new Set(cap?.deviceKeys || []));
        setKeyword('');
        setError('');
        if (devicesCache && archsCache) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([listAllDevices(), listProcessArchs()])
            .then(([ds, as]) => {
                devicesCache = ds;
                archsCache = as;
                if (!cancelled) {
                    setDevices(ds);
                    setArchs(as);
                }
            })
            .catch((e) => {
                if (!cancelled) setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // cap?.deviceKeys 随 capKey 一起变, 只需按 capKey 触发
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [capKey]);

    const archLabel = useMemo(() => {
        const m: Record<string, string> = {};
        archs.forEach((a) => {
            m[a.code] = `${a.icon || ''} ${a.label}`.trim();
        });
        return (code: string) => m[code] || code;
    }, [archs]);

    // 关键词过滤 (设备名 / deviceKey / 系列), 再按框架分组; 框架顺序跟 process_arch sort, 未登记的排最后
    const groups = useMemo(() => {
        const kw = keyword.trim().toLowerCase();
        const hit = (d: Device) =>
            !kw ||
            d.deviceKey.toLowerCase().includes(kw) ||
            (d.name || '').toLowerCase().includes(kw) ||
            (d.family || '').toLowerCase().includes(kw);
        const byArch = new Map<string, Device[]>();
        devices.filter(hit).forEach((d) => {
            const list = byArch.get(d.archCode) || [];
            list.push(d);
            byArch.set(d.archCode, list);
        });
        const order = new Map(archs.map((a, i) => [a.code, i] as const));
        return [...byArch.entries()].sort(
            ([a], [b]) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b),
        );
    }, [devices, archs, keyword]);

    const toggle = (key: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const setMany = (keys: string[], on: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
            return next;
        });
    };
    // 按工艺类型光源一键勾选: 老口径的迁移辅助 (设备能力组 source 等于该光源; ir 兼容设备侧历史值 red)
    const sameLightKeys = useMemo(() => {
        const light = (cap?.lightSource || '').toLowerCase();
        if (!light) return [];
        const accept = light === 'ir' ? ['ir', 'red'] : [light];
        return devices
            .filter((d) => accept.includes((d.capability?.source || '').toLowerCase()))
            .map((d) => d.deviceKey);
    }, [devices, cap?.lightSource]);

    const onSave = async () => {
        if (!cap) return;
        setBusy(true);
        try {
            const res = await updateCapType(cap.capKey, { deviceKeys: [...selected] });
            onSaved();
            onClose();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog('保存失败: ' + (e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)));
        } finally {
            setBusy(false);
        }
    };

    if (!cap) return null;
    const changed =
        selected.size !== (cap.deviceKeys || []).length ||
        (cap.deviceKeys || []).some((k) => !selected.has(k));
    return (
        <Modal open width={680} title={`绑定设备 · ${cap.label}`} onClose={onClose}>
            <div className="p-5 space-y-3">
                <div className="text-xs text-slate-500">
                    勾选具备「{cap.label}」工艺的设备; 不限框架, 非激光设备同样可绑。停用设备可先绑上,
                    徽标/反查只展示启用中的。已选 {selected.size} 台。
                </div>
                <div className="flex items-center gap-2">
                    <input
                        className="inp flex-1"
                        placeholder="搜设备名 / deviceKey / 系列…"
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                    {sameLightKeys.length > 0 && (
                        <button
                            type="button"
                            className="text-xs text-brand-600 hover:underline whitespace-nowrap"
                            title="勾选所有光源与本工艺类型一致的设备 (旧的自动反查口径)"
                            onClick={() => setMany(sameLightKeys, true)}
                        >
                            按光源勾选 ({sameLightKeys.length})
                        </button>
                    )}
                    <button
                        type="button"
                        className="text-xs text-slate-400 hover:text-slate-600 whitespace-nowrap"
                        onClick={() => setSelected(new Set())}
                    >
                        清空
                    </button>
                </div>

                {loading && <div className="text-sm text-slate-400 py-6 text-center">加载设备…</div>}
                {error && !loading && (
                    <div className="text-sm text-rose-600 py-4 text-center">加载失败: {error}</div>
                )}
                {!loading && !error && groups.length === 0 && (
                    <div className="text-sm text-slate-400 py-6 text-center">没有匹配的设备</div>
                )}
                {!loading && !error && groups.length > 0 && (
                    <div className="max-h-[52vh] overflow-auto border border-slate-100 rounded-lg divide-y divide-slate-100">
                        {groups.map(([arch, list]) => {
                            const keys = list.map((d) => d.deviceKey);
                            const picked = keys.filter((k) => selected.has(k)).length;
                            return (
                                <div key={arch} className="p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-xs font-semibold text-slate-600">
                                            {archLabel(arch)}
                                            <span className="text-slate-400 font-normal ml-1">
                                                {picked}/{list.length}
                                            </span>
                                        </div>
                                        <div className="flex gap-2 text-[11px]">
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() => setMany(keys, true)}
                                            >
                                                全选
                                            </button>
                                            <button
                                                type="button"
                                                className="text-slate-400 hover:text-slate-600"
                                                onClick={() => setMany(keys, false)}
                                            >
                                                取消
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1">
                                        {list.map((d) => (
                                            <label
                                                key={d.deviceKey}
                                                className={`flex items-center gap-1.5 text-xs cursor-pointer ${
                                                    d.status === 'enabled' ? 'text-slate-700' : 'text-slate-400'
                                                }`}
                                                title={`${d.deviceKey}${d.status === 'enabled' ? '' : ' (已停用)'}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(d.deviceKey)}
                                                    onChange={() => toggle(d.deviceKey)}
                                                />
                                                <span className="truncate">{deviceLabel(d, lightText)}</span>
                                                {d.status !== 'enabled' && (
                                                    <span className="tag bg-slate-100 text-slate-400 text-[10px]">停用</span>
                                                )}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <button type="button" className="btn-ghost" disabled={busy} onClick={onClose}>
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={busy || loading || !changed}
                        onClick={onSave}
                    >
                        {busy ? '保存中…' : '保存绑定'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default CapDeviceBindModal;
